<template>
  <DropdownMenuRoot v-model:open="isOpen">
    <DropdownMenuTrigger as-child :class="$attrs.class">
      <button
        v-motion
        type="button"
        :aria-label="$t('channel.admin.settings.customizeOptions')"
        class="size-40u flex bg-fill-brand-secondary rounded-full justify-center items-center"
        :initial="{ scale: 1, rotate: 0 }"
        :tapped="{ scale: 1.2, rotate: 15 }"
        :duration="600"
      >
        <Icon
          class="w-6 h-6 text-basics-primary"
          icon="ic:baseline-settings"
          aria-hidden="true"
        />
      </button>
    </DropdownMenuTrigger>

    <DropdownMenuPortal :to="portalRootElementSelector">
      <DropdownMenuContent
        class="outline-none shadow bg-fill-brand-secondary p-2 rounded-xl will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade z-[999999999999] w-[291px] backdrop-blur-[35px] text-text-on-brand font-sans text-sm flex flex-col gap-2"
        :side-offset="5"
      >
        <DropdownMenuItem
          class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand-secondary data-[highlighted]:text-text rounded-md px-2 py-2.5 gap-2.5 cursor-pointer"
          @click="emit('changeCoverImage')"
        >
          <Icon icon="mdi:image-360" width="24" height="24" />
          {{ $t("channel.admin.settings.changeCoverImage") }}
        </DropdownMenuItem>

        <DropdownMenuItem
          class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand-secondary data-[highlighted]:text-text rounded-md px-2 py-2.5 gap-2.5 cursor-pointer"
          @click="emit('changeLogo')"
        >
          <Icon icon="mdi:image-filter-hdr" width="24" height="24" />
          {{ $t("channel.admin.settings.changeLogo") }}
        </DropdownMenuItem>

        <DropdownMenuItem
          class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand-secondary data-[highlighted]:text-text rounded-md px-2 py-2.5 gap-2.5 cursor-pointer"
          @click="onUserManagement"
        >
          <Icon icon="mdi:users" width="24" height="24" />
          {{ $t("channel.admin.settings.userManagement") }}
        </DropdownMenuItem>

        <DropdownMenuItem
          class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand-secondary data-[highlighted]:text-text rounded-md px-2 py-2.5 gap-2.5 cursor-pointer"
          @click="emit('showCommunityManagement')"
        >
          <Icon icon="material-symbols:toggle-on" width="24" height="24" />

          {{ $t("channel.admin.settings.communityManagement") }}
        </DropdownMenuItem>

        <DropdownMenuItem
          class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand-secondary data-[highlighted]:text-text rounded-md px-2 py-2.5 gap-2.5"
          @click="onBehaviouralDataBrowser"
        >
          <Icon icon="mdi:users" width="24" height="24" />

          {{ $t("channel.admin.settings.behaviouralDataBrowser") }}
        </DropdownMenuItem>

        <DropdownMenuItem
          class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand-secondary data-[highlighted]:text-text rounded-md px-2 py-2.5 gap-2.5 cursor-pointer"
          @click="onManageApiKeys"
        >
          <Icon icon="material-symbols:key" width="24" height="24" />
          {{ $t("apiKeyManagement.title") }}
        </DropdownMenuItem>

        <DropdownMenuItem
          class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand-secondary data-[highlighted]:text-text rounded-md px-2 py-2.5 gap-2.5 cursor-pointer"
          @click="onAdminLeaderboard"
        >
          <Icon icon="mdi:trophy" width="24" height="24" />
          {{ $t("adminLeaderboard.title") }}
        </DropdownMenuItem>

        <DropdownMenuItem
          v-if="channelStore.permissions.manageOffers && enableCouponOffers"
          class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand-secondary data-[highlighted]:text-text rounded-md px-2 py-2.5 gap-2.5 cursor-pointer"
          @click="onManageOffers"
        >
          <Icon icon="mdi:ticket-percent" width="24" height="24" />
          {{ $t("channel.admin.settings.manageOffers") }}
        </DropdownMenuItem>

        <DropdownMenuItem
          class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand-secondary data-[highlighted]:text-text rounded-md px-2 py-2.5 gap-2.5 cursor-pointer"
          @click="onCustomizeChannel"
        >
          <Icon icon="mdi:palette" width="24" height="24" />
          {{ $t("customizeChannel.title") }}
        </DropdownMenuItem>

        <DropdownMenuItem
          class="flex items-center select-none outline-none data-[highlighted]:bg-fill-brand-secondary data-[highlighted]:text-text rounded-md px-2 py-2.5 gap-2.5 cursor-pointer"
          @click="onSsoManagement"
        >
          <Icon icon="mdi:shield-lock" width="24" height="24" />
          {{ $t("ssoManagement.title") }}
        </DropdownMenuItem>

        <DropdownMenuArrow class="fill-brand-bgdivider-20 backdrop-blur-3xl" />
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>

<script setup lang="ts">
import { portalRootElementSelector } from "@/constants/ui";
import { RouteNames } from "@/router/app.router";
import { useChannelStore } from "@/stores/channel.store";
import { Icon } from "@iconify/vue";
import {
  DropdownMenuArrow,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "reka-ui";
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useFeatureFlags } from "@/composables/use-feature-flags";

const channelStore = useChannelStore();
const { getBoolean } = useFeatureFlags();
const enableCouponOffers = getBoolean("enable_coupon_offers");

const emit = defineEmits<{
  delete: [];
  edit: [];
  share: [];
  changeLogo: [];
  changeCoverImage: [];
  modifyColorTheme: [];
  showCommunityManagement: [];
}>();

const isOpen = ref(false);

const router = useRouter();

function onUserManagement() {
  router.push({
    name: RouteNames.UserManagement,
    params: { channel: channelStore.channelSlug },
  });
}

function onBehaviouralDataBrowser() {
  router.push({
    name: RouteNames.BehaviouralDataBrowser,
    params: { channel: channelStore.channelSlug },
  });
}

function onManageApiKeys() {
  router.push({
    name: RouteNames.ApiKeyManagement,
    params: { channel: channelStore.channelSlug },
  });
}

function onAdminLeaderboard() {
  router.push({
    name: RouteNames.AdminLeaderboard,
    params: { channel: channelStore.channelSlug },
  });
}

function onSsoManagement() {
  router.push({
    name: RouteNames.SsoProviders,
    params: { channel: channelStore.channelSlug },
  });
}

function onManageOffers() {
  router.push({
    name: RouteNames.ManageOffers,
    params: { channel: channelStore.channelSlug },
  });
}

function onCustomizeChannel() {
  router.push({
    name: RouteNames.CustomizeChannel,
    params: { channel: channelStore.channelSlug },
  });
}
</script>
